import React, { createContext, useState, useContext, useCallback, useMemo } from 'react';
import translations from '../i18n';
import { SUPPORTED_LANGUAGES, DEFAULT_LANGUAGE } from '../utils/languageRouter';

const LanguageContext = createContext();
export const useLanguage = () => useContext(LanguageContext);

// ✅ 模組層級快取：key = `語言_翻譯key`，速度最快
const translationCache = new Map();

// ✅ 修正：cacheKey 改用 lang 前綴，而非昂貴的 Object.keys().join()
const lookupTranslation = (data, lang, key) => {
  const cacheKey = `${lang}::${key}`;
  if (translationCache.has(cacheKey)) return translationCache.get(cacheKey);

  const keys = key.split('.');
  let value = data;
  for (const k of keys) {
    if (value && typeof value === 'object' && k in value) {
      value = value[k];
    } else {
      translationCache.set(cacheKey, null);
      return null;
    }
  }
  translationCache.set(cacheKey, value);
  return value;
};

export const LanguageProvider = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState(() => {
    const saved = localStorage.getItem('language');
    return SUPPORTED_LANGUAGES.includes(saved) ? saved : DEFAULT_LANGUAGE;
  });

  const translationsData = useMemo(
    () => translations[currentLanguage] || {},
    [currentLanguage]
  );

  const changeLanguage = useCallback((lang) => {
    if (!SUPPORTED_LANGUAGES.includes(lang) || lang === currentLanguage) return;
    translationCache.clear(); // 語言切換時清除快取
    setCurrentLanguage(lang);
    localStorage.setItem('language', lang);
  }, [currentLanguage]);

  const t = useCallback((key, variables = {}) => {
    if (!key || typeof key !== 'string') return key || '';

    // ✅ 傳入 currentLanguage 作為 cacheKey 前綴，O(1) 查找
    const value = lookupTranslation(translationsData, currentLanguage, key);
    if (value === null || value === undefined) return key;

    if (typeof value === 'string' && Object.keys(variables).length > 0) {
      return value
        .replace(/\{\{(\w+)\}\}/g, (_, v) => variables[v] ?? `{{${v}}}`)
        .replace(/\{(\w+)\}/g, (_, v) => variables[v] ?? `{${v}}`);
    }

    return value || key;
  }, [translationsData, currentLanguage]);

  const value = useMemo(() => ({
    language: currentLanguage,
    currentLanguage,
    changeLanguage,
    setLanguage: changeLanguage,
    t,
  }), [currentLanguage, changeLanguage, t]);

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export default LanguageContext;